import React, { useState } from "react";
import { register as apiRegister } from "../services/auth";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Register() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    if (password !== confirmPassword) {
      setErr(t("auth.passwords_dont_match", "Passwords don't match"));
      return;
    }

    if (password.length < 6) {
      setErr(t("auth.password_too_short", "Password must be at least 6 characters"));
      return;
    }

    setLoading(true);
    try {
      await apiRegister(email, password, name);
      setRegistrationComplete(true);
    } catch (e: any) {
      setErr(e?.response?.data?.error || t("auth.registration_error", "Registration failed"));
    } finally {
      setLoading(false);
    }
  }

  if (registrationComplete) {
    return (
      <div className="flex min-h-screen">
        <div className="relative hidden overflow-hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute rounded-full top-20 left-20 w-72 h-72 bg-emerald-500 filter blur-3xl"></div>
            <div className="absolute bg-blue-500 rounded-full bottom-20 right-20 w-96 h-96 filter blur-3xl"></div>
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
            <div className="text-center">
              <img src="/assets/ig_v_color.png" alt="iGrowth" className="w-56 h-auto mx-auto mb-4" />
              <p className="mb-8 text-xl text-slate-300">
                {t("auth.platform_tagline", "Sports Club Management Platform")}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full lg:w-1/2 bg-slate-900">
          <div className="absolute top-4 right-4">
            <LanguageSwitcher />
          </div>

          <div className="flex items-center justify-center flex-1 p-8">
            <div className="w-full max-w-md text-center">
              <div className="mb-8 text-center lg:hidden">
                <img src="/assets/ig_h_white.png" alt="iGrowth" className="w-56 h-auto mx-auto mb-4" />
              </div>

              <div className="p-8 border shadow-xl bg-slate-800/50 backdrop-blur-sm rounded-2xl border-slate-700/50">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20">
                  <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                  </svg>
                </div>

                <h2 className="mb-4 text-2xl font-semibold text-white">
                  {t("auth.check_email", "Check your email")}
                </h2>
                <p className="mb-6 text-slate-400">
                  {t("auth.verification_sent", "We've sent a verification link to")}
                  <br />
                  <span className="font-medium text-emerald-400">{email}</span>
                </p>
                <p className="mb-6 text-sm text-slate-500">
                  {t("auth.verification_instructions", "Click the link in the email to activate your account. If you don't see the email, check your spam folder.")}
                </p>

                <Link
                  to="/login"
                  className="inline-block w-full px-4 py-3 font-medium text-white transition-all duration-200 rounded-lg bg-emerald-600 hover:bg-emerald-500"
                >
                  {t("auth.go_to_login", "Go to Login")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden overflow-hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute rounded-full top-20 left-20 w-72 h-72 bg-emerald-500 filter blur-3xl"></div>
          <div className="absolute bg-blue-500 rounded-full bottom-20 right-20 w-96 h-96 filter blur-3xl"></div>
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          <div className="text-center">
            <h1 className="mb-4 text-5xl font-bold text-white">
              <span className="text-emerald-400">i</span>GROWth
            </h1>
            <p className="mb-8 text-xl text-slate-300">
              {t("auth.platform_tagline", "Sports Club Management Platform")}
            </p>
            <div className="flex flex-col gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/20">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span>{t("auth.feature_track", "Track your training progress")}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/20">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span>{t("auth.feature_learn", "Learn with quizzes & videos")}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/20">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span>{t("auth.feature_rewards", "Earn points & rewards")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-full lg:w-1/2 bg-slate-900">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div className="flex items-center justify-center flex-1 p-8">
          <div className="w-full max-w-md">
            <div className="mb-8 text-center lg:hidden">
              <img src="/assets/ig_v_color.png" alt="iGrowth" className="w-56 h-auto mx-auto mb-4 " />
            </div>

            <div className="p-8 border shadow-xl bg-slate-800/50 backdrop-blur-sm rounded-2xl border-slate-700/50">
              <h2 className="mb-2 text-2xl font-semibold text-white">{t("auth.create_account", "Create Account")}</h2>
              <p className="mb-6 text-slate-400">{t("auth.register_subtitle", "Join as an athlete")}</p>

              <div className="p-4 mb-6 border rounded-lg bg-blue-500/10 border-blue-500/30">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-300">
                    {t("auth.athlete_only_notice", "This registration is for athletes only. Coaches and administrators are invited by club admins.")}
                  </p>
                </div>
              </div>

              {err && (
                <div className="p-3 mb-4 text-sm text-red-400 border rounded-lg bg-red-500/10 border-red-500/30">
                  {err}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-300">
                    {t("common.name", "Full Name")}
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder={t("common.name", "Full Name")!}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-300">
                    {t("common.email", "Email")}
                  </label>
                  <input
                    type="email"
                    name="register-email"
                    autoComplete="off"
                    className="w-full px-4 py-3 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder={t("common.email", "Email")!}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-300">
                    {t("common.password", "Password")}
                  </label>
                  <input
                    type="password"
                    name="register-password"
                    autoComplete="new-password"
                    className="w-full px-4 py-3 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder={t("common.password", "Password")!}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-300">
                    {t("auth.confirm_password", "Confirm Password")}
                  </label>
                  <input
                    type="password"
                    name="register-confirm"
                    autoComplete="new-password"
                    className="w-full px-4 py-3 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder={t("auth.confirm_password", "Confirm Password")!}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-3 font-medium text-white transition-all duration-200 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t("common.loading", "Loading...") : t("common.register", "Register")}
                </button>
              </form>

              <div className="mt-6 text-sm text-center text-slate-400">
                {t("auth.have_account", "Already have an account?")}{" "}
                <Link to="/login" className="transition-colors text-emerald-400 hover:text-emerald-300">
                  {t("common.login", "Login")}
                </Link>
              </div>
              <div className="mt-2 text-sm text-center text-slate-400">
                {t("auth.club_admin_register", "Ești administrator de club?")}{" "}
                <Link to="/register-club" className="transition-colors text-blue-400 hover:text-blue-300">
                  {t("auth.register_club_link", "Înregistrează clubul")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
